import { useMemo } from 'react'
import { keyBy } from 'es-toolkit'
import useI18n from '@/shared/hooks/useI18n'
import FinalizedReportDeclarantsBanner from '@/features/dashboard/pages/finalized-reports/finalized-report-page/components/FinalizedReportDeclarantsBanner'
import { MesReportPayload } from '@/shared/hooks/api/integrations'
import MesReportCoverPageZh from './MesReportCoverPage.zh'
import FinalizedReportPage from '@/features/dashboard/pages/finalized-reports/finalized-report-page/components/FinalizedReportPage'
import FinalizedReportDetailsTable from '@/features/dashboard/pages/finalized-reports/finalized-report-page/components/FinalizedReportDetailsTable'
import FinalizedReportSpecificEmbeddedEmissionsTable from '@/features/dashboard/pages/finalized-reports/finalized-report-page/components/FinalizedReportSpecificEmbeddedEmissionsTable'
import FinalizedReportCombinedElectricityEmissionsTable from '@/features/dashboard/pages/finalized-reports/finalized-report-page/components/FinalizedReportCombinedElectricityEmissionsTable'
import FinalizedReportElectricityEmissionsTable from '@/features/dashboard/pages/finalized-reports/finalized-report-page/components/FinalizedReportElectricityEmissionsTable'
import { Installation } from '@/features/installations/api/InstallationModel'
import { ProductionRoute } from '@/features/production-routes/api/ProductionRouteModel'
import { OperatorOrganization } from '@/features/auth/api/AuthModel'

import {
  DocumentPageContent,
  DocumentPageSubSection,
  SectionTitle,
  SubSectionTitle
} from './MesReportEmbed'
import { TableCell, TableRow } from '@/common'
import {
  TableBody,
  TableBodyRow
} from '@/features/dashboard/pages/finalized-reports/finalized-report-page/components/typography'

type EntityConfig = {
  entityName: string
  street: string
  city: string
  country: string
  email: string
  phone: string
  website: string
}

function Qpd23Element({
  emissionQualifyingParamsNonAluminium,
  numberFormatter
}: {
  emissionQualifyingParamsNonAluminium: { label: string; value: number }[]
  label: string
  numberFormatter: (value: number) => string
}) {
  const tableElements = emissionQualifyingParamsNonAluminium.filter(element => element.value > 0)
  const totalPercentage = numberFormatter(
    tableElements.reduce((acc, element) => acc + element.value, 0)
  )
  return (
    <div className="space-y-4">
      <p>- 用于CBAM报告QPD23的目的，使用了{totalPercentage} %的非铝材料</p>

      <table className="w-full text-left border">
        <thead className="bg-background-main text-text-secondary text-sm">
          <tr>
            <th className="py-2 px-4">材料</th>
            <th className="py-2 px-4">百分比</th>
          </tr>
        </thead>
        <tbody>
          {tableElements.map(param => (
            <tr key={param.label} className="border-t">
              <td className="py-2 px-4 font-semibold">{param.label}</td>
              <td className="py-2 px-4">{numberFormatter(param.value)}%</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <TableRow>
            <TableCell className="font-bold">总计</TableCell>
            <TableCell className="font-bold">{totalPercentage}%</TableCell>
          </TableRow>
        </tfoot>
      </table>
    </div>
  )
}

const MES_REPORT_CONFIG: Record<string, EntityConfig> = {
  artem: {
    entityName: 'ARTEM Technologie AG',
    street: 'Sinserstraße 67',
    city: '6330 Cham Zug',
    country: 'Switzerland',
    email: 'info@artem.earth',
    phone: '+41 41 560 55 00',
    website: 'https://artem.earth'
  },
  zh: {
    entityName: 'ARTEM Technologie AG',
    street: 'Sinserstraße 67',
    city: '6330 Cham Zug',
    country: 'Switzerland',
    email: 'info@artem.earth',
    phone: '+41 41 560 55 00',
    website: 'https://artem.earth'
  }
}

export default function MesReportEmbed({
  data: {
    electricityEmissionFactor,
    emissionQualifyingParams,
    isFullCalendarYear,
    methodAggregatedEmissions
  },
  isPreview,
  installation,
  productionRoute,
  organization,
  startDate,
  endDate
}: {
  data: MesReportPayload
  isPreview?: boolean
  installation: Installation
  productionRoute: ProductionRoute
  organization: OperatorOrganization
  startDate: string
  endDate: string
}) {
  const finalizedAt = new Date().toISOString()
  const { t, dateLongFormatterGB, numberFormatter4Decimals } = useI18n()

  const aggregatedElectricity = {
    sources: [
      {
        type: 'GRID',
        totalConsumptionShare: 1,
        weightedAvgEmissionFactor: Number(electricityEmissionFactor)
      }
    ]
  }

  const operatorName = organization.name
  const installationName = installation.name
  const country = t(`countriesByIsoAlpha2:${installation.country}`)

  const installationCountryName = t(`countriesByIsoAlpha2:${installation.country}`)
  const organizationCountryName = t(`countriesByIsoAlpha2:${organization.country}`)
  const unlocodeWithCountry = installation.unlocode
    ? `${installation.country.toUpperCase()} ${installation.unlocode}`
    : null

  const sourcesByType = keyBy(aggregatedElectricity.sources, source => source.type)
  const isUsingDirectTechnicalLink = !!sourcesByType['DIRECT_TECHNICAL_LINK']
  const isUsingPPA = !!sourcesByType['PPA']

  const installationDetails = [
    {
      title: '安装名称',
      value: installationName
    },
    {
      title: '街道',
      value: installation.street
    },
    {
      title: '街道2',
      value: installation.street2
    },
    {
      title: '邮政编码',
      value: installation.postCode
    },
    {
      title: '城市',
      value: installation.city
    },
    {
      title: '国家',
      value: installationCountryName
    },
    {
      title: '联合国编码',
      value: unlocodeWithCountry
    },
    {
      title: '经度',
      value: installation.longitude
    },
    {
      title: '纬度',
      value: installation.latitude
    }
  ]

  const organizationDetails = [
    {
      title: 'Street',
      value: organization.street
    },
    {
      title: '街道号',
      value: organization.streetNumber
    },
    {
      title: '街道2',
      value: organization.street2
    },
    {
      title: '邮政编码',
      value: organization.postCode
    },
    {
      title: '城市',
      value: organization.city
    },
    {
      title: '邮政信箱',
      value: organization.poBox
    },
    {
      title: '国家',
      value: organizationCountryName
    }
  ]

  const contactDetails = [
    {
      title: '联系人姓名',
      value: organization.contactName
    },
    {
      title: '电话号码',
      value: organization.contactPhoneNumber
    },
    {
      title: '电子邮箱',
      value: organization.contactEmail
    }
  ]

  const startDateFormatted = dateLongFormatterGB(new Date(startDate))
  const endDateFormatted = dateLongFormatterGB(new Date(endDate))
  const creationDateFormatted = dateLongFormatterGB(new Date())
  const finalizedDateFormatted = useMemo(() => {
    if (!finalizedAt) {
      return 'TBA'
    }

    const date = new Date(finalizedAt)

    return dateLongFormatterGB(date)
  }, [finalizedAt, dateLongFormatterGB])

  const electricityCombined = aggregatedElectricity.sources.reduce(
    (acc, source) => {
      return {
        totalConsumptionShare: acc.totalConsumptionShare + source.totalConsumptionShare,
        weightedAvgEmissionFactor:
          acc.weightedAvgEmissionFactor +
          source.weightedAvgEmissionFactor * source.totalConsumptionShare
      }
    },
    { totalConsumptionShare: 0, weightedAvgEmissionFactor: 0 }
  )

  const cnCodes = productionRoute.productionMethods.flatMap(method =>
    method.cnCodes.map(cnCode => ({
      methodCode: method.code,
      cnCode: cnCode
    }))
  )

  const emissionQualifyingParamsArray = Object.keys(emissionQualifyingParams) as Array<
    keyof typeof emissionQualifyingParams
  >

  const emissionQualifyingParamsNonAluminium = useMemo(
    () =>
      emissionQualifyingParamsArray
        .filter(param => param === 'QPD23')
        .map(param => emissionQualifyingParams[param]?.elements)
        .flatMap(elements =>
          elements
            ? elements.map(element => ({
                label: element.name,
                value: element.value
              }))
            : []
        ),
    [emissionQualifyingParams, emissionQualifyingParamsArray]
  )

  const formattedQpd21 = numberFormatter4Decimals(emissionQualifyingParams.QPD21?.value ?? 0)
  const qpd21 = <>- 每吨产品使用{formattedQpd21}吨废料。本评估不包括用于生产二次铝前驱体的废料。</>
  const qpd22 = (
    <>
      -
      设施内用于生产过程的所有废料均为预消费废料，占所有废料输入的100%。此评估不包括用于生产再生铝前驱体的废料。
    </>
  )
  const qpd23 = (
    <Qpd23Element
      emissionQualifyingParamsNonAluminium={emissionQualifyingParamsNonAluminium}
      label={t('emissionQualifyingParams:fields.QPD23.reportText')}
      numberFormatter={numberFormatter4Decimals}
    />
  )

  const footNote = isFullCalendarYear
    ? null
    : 'Please note that the reporting period of this report is less than a full calendar year. Additional confirmation may be required to verify that this period is permitted pursuant to the CBAM Regulation.'

  return (
    <div className="max-w-4xl mx-auto text-sm mb-10">
      {/* 封面页 */}
      <MesReportCoverPageZh
        operatorName={operatorName}
        cnCodes={cnCodes ?? []}
        startDate={startDateFormatted}
        endDate={endDateFormatted}
        creationDate={creationDateFormatted}
        finalizedDate={finalizedDateFormatted}
        productionRouteName={productionRoute.name}
        entityConfig={MES_REPORT_CONFIG.zh}
      />

      {/* 第1页：简介 + 安装与生产路线 */}
      <FinalizedReportPage
        isPreview={isPreview}
        pageNumber="1"
        totalPage="5"
        entityConfig={MES_REPORT_CONFIG.zh}
        footNote={footNote}
      >
        <DocumentPageContent>
          <section className="space-y-4">
            <SectionTitle>1. 简介</SectionTitle>
            <p>
              本排放报告由 {operatorName}（"运营商"）生成，旨在支持根据2023年5月10日第(EU)
              2023/956号法规和2023年8月17日第(EU)
              2023/1773号实施条例（"CBAM法规"）建立的碳边境调节机制（"CBAM"）下申报人所做的申报。
            </p>
            <p>
              本排放报告由运营商通过ARTEM
              Operators生成，这是一种简化合规流程的易用解决方案。所有数据均已标准化，申报人可直接将本报告中的数据输入欧盟申报人门户，无需进一步计算。
            </p>
            <p>
              若运营商对其活动数据、排放因子、计算因子及所购前体的嵌入排放做出假设，这些假设将在本报告中明确列出。
            </p>
            <p>
              本报告已由运营商最终确定，并在 {finalizedDateFormatted}{' '}
              时准确无误。在本报告最终确定时，据运营商所知，本报告不包含任何错误、遗漏或重大虚假陈述。
            </p>
          </section>
          <section className="space-y-4">
            <header className="space-y-2">
              <SectionTitle>2. 安装与生产路线</SectionTitle>
            </header>

            <DocumentPageSubSection>
              <SubSectionTitle>a. 地址</SubSectionTitle>
              <p>生产本报告相关产品的安装具有以下详细信息：</p>

              <FinalizedReportDetailsTable items={installationDetails} />
            </DocumentPageSubSection>

            <DocumentPageSubSection>
              <SubSectionTitle>b. 生产路线</SubSectionTitle>
              <p>
                本排放报告涉及以下由 {productionRoute.name} 生产路线（"产品"）生产的{' '}
                {t(`common:aggregatedGoods.${productionRoute.aggregatedGood}`)}：
              </p>
              <p>产品具有以下CN编码：</p>
              <ul className="list-disc pl-5 mb-4 font-semibold">
                {cnCodes.map(method => (
                  <li key={method.methodCode}>{method.cnCode}</li>
                ))}
              </ul>
              <p>
                下述排放数据和附加支持信息适用于所有产品。为便于申报人录入欧盟门户，以下数据已按每吨产品标准化，无需进一步计算。
              </p>
            </DocumentPageSubSection>
          </section>
        </DocumentPageContent>
      </FinalizedReportPage>

      {/* 第2页：排放 */}
      <FinalizedReportPage
        isPreview={isPreview}
        pageNumber="2"
        totalPage="5"
        entityConfig={MES_REPORT_CONFIG.zh}
        footNote={footNote}
      >
        <DocumentPageContent>
          <SectionTitle>3. 排放</SectionTitle>

          {/* a节 */}
          <DocumentPageSubSection>
            <SubSectionTitle>a. 特定嵌入排放</SubSectionTitle>
            <p>运营商已按如下方式计算产品的特定嵌入排放：</p>

            <FinalizedReportSpecificEmbeddedEmissionsTable
              columnNames={{
                specificEmbeddedDirect: '特定直接嵌入排放',
                specificEmbeddedIndirect: '特定间接嵌入排放'
              }}
              methodAggregatedEmissions={methodAggregatedEmissions[0]}
            />

            <p className="text-xs italic text-text-secondary mb-8">
              该生产路线的排放数据已通过ARTEM运营商平台收集，运营商平台已提供关于原料溯源的相關数据，所报告的具体直接和间接排放涵盖了前体排放和现场排放并且符合CBAM法规的要求。
            </p>
          </DocumentPageSubSection>

          {/* b节 */}
          <DocumentPageSubSection>
            <SubSectionTitle>b. 特定间接嵌入排放</SubSectionTitle>
            <p>产品的特定间接嵌入排放具有以下信息：</p>
            <FinalizedReportCombinedElectricityEmissionsTable
              strings={{
                onSite: '现场',
                precursor: '前体',
                total: '合计',
                electricityConsumed: '用电量',
                emissionFactor: '排放因子',
                specificIndirectEmissions: '特定间接嵌入排放'
              }}
              showOnlyCombinedEmissions
              methodAggregatedEmissions={methodAggregatedEmissions[0]}
            />
            <p>运营商的安装级电力是基于以下电力来源的组合计算的：</p>
            <FinalizedReportElectricityEmissionsTable
              columnNames={{
                electricitySource: '电力来源',
                consumptionShare: '用电占比',
                emissionFactor: '排放因子'
              }}
              combinedLabel="合计"
              electricityCombined={electricityCombined}
              aggregatedElectricity={aggregatedElectricity}
            />

            <div className="space-y-4">
              <p>电网发电量按{country}的IEA最新数据默认排放因子计算。</p>
              {isUsingDirectTechnicalLink && (
                <p>
                  运营商已提供以下关于安装直接技术连接的信息：
                  <br />
                  {
                    productionRoute.electricityProductionMethods.find(
                      pm => pm.type === 'DIRECT_TECHNICAL_LINK'
                    )?.info
                  }
                </p>
              )}
              {isUsingPPA && (
                <p>
                  运营商已提供以下关于双边购电协议的信息：
                  {productionRoute.electricityProductionMethods.find(pm => pm.type === 'PPA')?.info}
                </p>
              )}

              <p>申报人可以在欧盟门户中输入以下文本，以报告间接排放：</p>
              <div className="space-y-2 bg-gray-100 p-2 italic">
                <p>
                  前体排放因子是根据欧盟委员会提供的特定国家电网排放因子计算的。电力消耗是根据前体特定的间接嵌入式排放量及相应的排放因子得出的。
                  运营方已根据装置的能源组合中来自电网的电力，在装置层面计算了加权平均排放因子。对于电网电力，采用了国际能源署（IEA）的排放因子。
                </p>
              </div>
            </div>
          </DocumentPageSubSection>
        </DocumentPageContent>
      </FinalizedReportPage>

      {/* 第3页：附加支持信息 */}
      <FinalizedReportPage
        isPreview={isPreview}
        pageNumber="3"
        totalPage="5"
        entityConfig={MES_REPORT_CONFIG.zh}
        footNote={footNote}
      >
        <DocumentPageContent>
          <SectionTitle>4. 附加支持信息</SectionTitle>

          {/* a节 */}
          <div className="space-y-2">
            <SubSectionTitle>a. 合格参数</SubSectionTitle>
            <p>产品具有以下部门特定信息，需用于报告：</p>
            <ul className="space-y-1.5">
              <li>{qpd21}</li>
              <li>{qpd22}</li>
              <li>{qpd23}</li>
            </ul>
          </div>
        </DocumentPageContent>
      </FinalizedReportPage>

      {/* 第4页：附加支持信息 */}
      <FinalizedReportPage
        isPreview={isPreview}
        pageNumber="4"
        totalPage="5"
        entityConfig={MES_REPORT_CONFIG.zh}
        footNote={footNote}
      >
        <DocumentPageContent>
          {/* b节 */}
          <DocumentPageSubSection>
            <SubSectionTitle>b. 监测方法与数据质量</SubSectionTitle>
            <p>运营商根据其测量系统获得的活动数据和标准值的计算因子确定其排放。</p>
            <p>
              运营商使用以下数据源计算所需的活动数据。数据源根据CBAM法规的要求，按以下优先级选择：(i)
              计量/测量数据，(ii) 发票/账单数据：
            </p>
            <table className="w-full text-left  mb-4">
              <thead className="text-text-secondary text-sm">
                <tr>
                  <th className="py-2 px-4 text-black">数据类型</th>
                  <th className="py-2 px-4 text-black">优先级</th>
                  <th className="py-2 px-4 text-black">数据说明</th>
                </tr>
              </thead>
              <TableBody>
                <TableBodyRow>
                  <td className="py-2 px-4">计量/测量数据</td>
                  <td className="py-2 px-4">最高优先级</td>
                  <td className="py-2 px-4">
                    <p>直接燃料消耗（如燃气/柴油表、油罐记录）。</p>
                    <p>现场用电（电表/分表）。</p>
                  </td>
                </TableBodyRow>
                <TableBodyRow>
                  <td className="py-2 px-4">发票/账单数据</td>
                  <td className="py-2 px-4">第二优先级</td>
                  <td className="py-2 px-4">
                    <p>经核实的燃料采购发票（体积/重量）。</p>
                    <p>来自公用事业公司的电费账单（千瓦时）。</p>
                  </td>
                </TableBodyRow>
              </TableBody>
            </table>
            <p>
              运营商保留燃料/电力发票（含日期、体积、供应商信息）和仪表读数（带时间戳和校准记录）的数字/纸质记录。如数据缺失或无法验证，运营商将采用保守估算（上限值）并记录假设。
            </p>
            <p>运营商目前正在通过ARTEM Operators实施电子数据日志，以输入数据源并执行计算步骤。</p>

            <p>运营商还计划由ARTEM进行年度第三方预验证。</p>

            <p>运营商对其数据质量有合理保证，理由如下：</p>

            <p>(1) 本报告所依据的数据来自运营商制造执行系统的相关提取。</p>

            <p>
              (2)
              运营商代表已审核本报告，并确认其对排放量的计算在法规允许的最大误差范围内是公正合理的。
            </p>
          </DocumentPageSubSection>

          {/* c节 */}
          <DocumentPageSubSection>
            <SubSectionTitle>c. 活动水平验证</SubSectionTitle>
            <p>
              在制定监测报告与验证文件的同时，运营商计划改进其监测流程，并考虑逐步引入物联网传感器/自动化监测以实现实时数据采集，并加强员工数据收集培训。
            </p>
            <p>
              运营商预计其生产协议将在未来报告期内符合欧洲标准EN
              45557，分别核算前体、废料及其他物料输入，以及最终产品、内部转换、废弃物和回库。
            </p>
          </DocumentPageSubSection>

          {/* d节 */}
          <DocumentPageSubSection>
            <SubSectionTitle>d. 碳价支付</SubSectionTitle>
            <p>产品原产国尚未支付可用于抵消CBAM责任的碳价。</p>
          </DocumentPageSubSection>
        </DocumentPageContent>
      </FinalizedReportPage>

      {/* 第5页：组织联系信息 */}
      <FinalizedReportPage
        isPreview={isPreview}
        pageNumber="5"
        totalPage="5"
        entityConfig={MES_REPORT_CONFIG.zh}
        footNote={footNote}
      >
        <DocumentPageContent>
          <section className="space-y-2">
            <header className="space-y-2">
              <SectionTitle>5. 组织联系信息</SectionTitle>
              <p className="mt-2 mb-4">组织联系信息及联系人：</p>
            </header>
            <div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <SubSectionTitle>a. 组织名称</SubSectionTitle>
                  <FinalizedReportDetailsTable
                    items={[
                      {
                        title: '组织名称',
                        value: organization.name
                      }
                    ]}
                  />
                </div>
                <div className="space-y-2">
                  <SubSectionTitle>b. 运营商地址</SubSectionTitle>
                  <FinalizedReportDetailsTable items={organizationDetails} />
                </div>
                <div className="space-y-2">
                  <SubSectionTitle>c. 运营商联系信息</SubSectionTitle>
                  <FinalizedReportDetailsTable items={contactDetails} />
                </div>
              </div>
            </div>
          </section>

          <section className="space-y-2">
            <SectionTitle>6. 关于ARTEM</SectionTitle>
            <p>
              ARTEM
              Operators是一款CBAM合规解决方案，帮助全球运营商满足CBAM要求，并自动化数据采集，减少手动输入错误的可能性。
            </p>
            <p>
              如需获取本年度运营商排放预测、验证准备情况及排放数据质量洞察，欢迎注册ARTEM
              Declarants！
            </p>
            <p>ARTEM Declarants根据最新运营商数据持续更新排放预测，让您对CBAM合规充满信心。</p>
          </section>
          <FinalizedReportDeclarantsBanner />
        </DocumentPageContent>

        {/* 请勿删除以下行，PDF渲染需要 */}
        <div data-artem="render-completed"></div>
      </FinalizedReportPage>
    </div>
  )
}
